import {
  ScaffolderFieldError,
  ScaffolderFormHeader,
  ScaffolderFormPaper,
  ScaffolderInputHeader,
} from '@internal/plugin-scaffolder-common-field-react';
import React, { useEffect, useRef, useState } from 'react';

import { Entity, stringifyEntityRef } from '@backstage/catalog-model';

import {
  Backdrop,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';

import { useGetDaasEntities } from '../../../hooks/useGetDaasEntities/useGetDaasEntities';
import {
  APPLICATION_DESCRIPTION_INFO,
  APPLICATION_NAME_INFO,
} from '../infoDoc';
import { useStyles } from '../styles';
import { DaasApplicationInputData } from '../types';
import { convertEntityToApplicationInput } from '../utils';

interface ApplicationEditBasicInfoSectionProps {
  value: {
    applicationName: string;
    description: string;
    applicationRef?: string;
  };
  onChange: (value: {
    applicationName: string;
    description: string;
    applicationRef?: string;
  }) => void;
  onApplicationEntitySelect: (data: Partial<DaasApplicationInputData>) => void;
  isFormAlreadyPopulated?: boolean;
  errors?: string[];
}

const findApplicationEntity = (
  entities: Entity[],
  applicationRef: string | undefined,
): Entity | null => {
  if (applicationRef) {
    return entities.find(e => stringifyEntityRef(e) === applicationRef) ?? null;
  }
  return null;
};

const ApplicationEditBasicInfoSectionInner = ({
  value,
  onChange,
  onApplicationEntitySelect,
  isFormAlreadyPopulated = false,
  errors = [],
}: ApplicationEditBasicInfoSectionProps) => {
  const classes = useStyles();
  const { value: applicationEntities = [], loading: applicationsLoading } =
    useGetDaasEntities('daas-application', false, true);

  const selectedEntity =
    value.applicationName || value.applicationRef
      ? findApplicationEntity(applicationEntities, value.applicationRef)
      : null;

  const [inaccessibleApplicationName, setInaccessibleApplicationName] =
    useState<string | null>(null);
  const hasAutoPrefilled = useRef(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  const applicationNotAccessible =
    Boolean(inaccessibleApplicationName) ||
    (errors.length > 0 && !value.applicationName);

  useEffect(() => {
    if (applicationNotAccessible) {
      sectionRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    }
  }, [applicationNotAccessible]);

  useEffect(() => {
    if (
      (!value.applicationName && !value.applicationRef) ||
      applicationsLoading
    )
      return;
    const entity = findApplicationEntity(
      applicationEntities,
      value.applicationRef,
    );
    if (!entity) {
      setInaccessibleApplicationName(value.applicationName);
      return;
    }
    setInaccessibleApplicationName(null);
    if (hasAutoPrefilled.current || isFormAlreadyPopulated) return;
    hasAutoPrefilled.current = true;
    const fullData = convertEntityToApplicationInput(entity);
    onChange({
      applicationName: fullData.applicationName || value.applicationName,
      description: fullData.description || '',
      applicationRef: stringifyEntityRef(entity),
    });
    onApplicationEntitySelect(fullData);
  }, [
    value.applicationName,
    value.applicationRef,
    applicationEntities,
    applicationsLoading,
    isFormAlreadyPopulated,
    onApplicationEntitySelect,
    onChange,
  ]);

  const handleEntitySelect = (_: any, entity: Entity | null) => {
    if (!entity) return;
    setInaccessibleApplicationName(null);
    hasAutoPrefilled.current = true;
    const fullData = convertEntityToApplicationInput(entity);
    onChange({
      applicationName: fullData.applicationName || entity.metadata.name,
      description: fullData.description || '',
      applicationRef: stringifyEntityRef(entity),
    });
    onApplicationEntitySelect(fullData);
  };

  return (
    <Box>
      <ScaffolderFormPaper>
        <ScaffolderFormHeader
          title="Application configuration"
          description="Select an application to edit its configuration."
          divider
        />
        <Backdrop
          open={applicationsLoading}
          className={classes.loadingProgress}
        >
          <CircularProgress size={48} />
        </Backdrop>
        <div ref={sectionRef}>
          <Box className={classes.inputContainer}>
            <ScaffolderInputHeader
              title="Application to edit"
              description="Select the application you want to edit."
              infoDoc={APPLICATION_NAME_INFO}
            />
            <Autocomplete
              size="small"
              value={selectedEntity}
              loading={applicationsLoading}
              onChange={handleEntitySelect}
              options={applicationEntities}
              disableClearable
              getOptionLabel={option =>
                option.metadata.title || option.metadata.name
              }
              renderInput={params => {
                let applicationHelperText: React.ReactNode;
                if (inaccessibleApplicationName) {
                  applicationHelperText = (
                    <ScaffolderFieldError
                      message={`You do not have permission to edit application "${inaccessibleApplicationName}". Select a different application.`}
                    />
                  );
                } else if (applicationNotAccessible) {
                  applicationHelperText = (
                    <ScaffolderFieldError message="Application is required" />
                  );
                }
                return (
                  <TextField
                    {...params}
                    label="Application"
                    variant="outlined"
                    margin="none"
                    color="secondary"
                    required
                    InputProps={params.InputProps}
                    error={applicationNotAccessible}
                    helperText={applicationHelperText}
                  />
                );
              }}
              renderOption={option => (
                <Box>
                  <Typography variant="body2" noWrap>
                    {option.metadata.title || option.metadata.name}
                  </Typography>
                  {option.metadata.title && (
                    <Typography variant="caption" noWrap color="textSecondary">
                      {option.metadata.name}
                    </Typography>
                  )}
                </Box>
              )}
              noOptionsText={
                applicationsLoading
                  ? 'Loading applications...'
                  : 'No accessible applications found'
              }
              loadingText="Loading applications..."
            />
          </Box>
        </div>
        <Box className={classes.inputContainer}>
          <ScaffolderInputHeader
            title="Description"
            description="Specify a description of the application."
            infoDoc={APPLICATION_DESCRIPTION_INFO}
          />
          <TextField
            label="Description"
            value={value.description}
            onChange={e => onChange({ ...value, description: e.target.value })}
            variant="outlined"
            color="secondary"
            size="small"
            fullWidth
            multiline
            inputProps={{ maxLength: 250 }}
            minRows={3}
            helperText="Optional. Up to 250 characters."
          />
        </Box>
      </ScaffolderFormPaper>
    </Box>
  );
};

export const ApplicationEditBasicInfoSection = React.memo(
  ApplicationEditBasicInfoSectionInner,
);
